/**
 * chatDraftStore — Rascunhos do composer por conversa (singleton fora do React).
 *
 * Texto: persistido em localStorage (chave única `chat_drafts`), write-through
 * SÍNCRONO a cada alteração — nunca via useEffect pós-render (navigate pode
 * desmontar antes do efeito rodar; mesma lição do useChatPreferences).
 * Mídia (anexos/áudio/reply/staged): apenas em memória — sobrevive à troca de
 * conversa, não ao reload (blobs não cabem em localStorage).
 */

import type { ConversationMessageResponse, QuickMessageContent } from '@troia-v3/types';
import type { AttachmentFile } from '../hooks/useFileAttachment';
import type { AudioRecordingData } from '../hooks/useAudioRecording';

export interface ComposerDraft {
  attachments: AttachmentFile[];
  audioData: AudioRecordingData | null;
  replyingToMessage: ConversationMessageResponse | null;
  stagedQuickMessageMedia: QuickMessageContent[] | null;
}

export interface DraftSummary {
  /** Texto do rascunho (null quando só há mídia) */
  text: string | null;
  /** Há anexos/áudio/mídia staged pendentes (reply sozinho NÃO conta) */
  hasMedia: boolean;
}

interface StoredTextDraft {
  text: string;
  updatedAt: number;
}

const STORAGE_KEY = 'chat_drafts';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_ENTRIES = 100;

const loadTextDrafts = (): Record<string, StoredTextDraft> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const now = Date.now();
    const valid: Record<string, StoredTextDraft> = {};
    for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
      const v = value as Partial<StoredTextDraft> | null;
      if (
        v &&
        typeof v.text === 'string' &&
        v.text.trim() !== '' &&
        typeof v.updatedAt === 'number' &&
        now - v.updatedAt <= MAX_AGE_MS
      ) {
        valid[id] = { text: v.text, updatedAt: v.updatedAt };
      }
    }
    return valid;
  } catch {
    return {};
  }
};

let textDrafts: Record<string, StoredTextDraft> = loadTextDrafts();
const composerDrafts = new Map<string, ComposerDraft>();
const summaryCache = new Map<string, DraftSummary | null>();
const listeners = new Set<() => void>();

const persist = (): void => {
  try {
    if (Object.keys(textDrafts).length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(textDrafts));
    }
  } catch {
    // Quota/modo privado: degrada para memória da sessão (feature segue funcional)
  }
};

const emit = (): void => {
  listeners.forEach((listener) => listener());
};

const invalidate = (conversationId: string): void => {
  summaryCache.delete(conversationId);
  emit();
};

const isComposerEmpty = (draft: ComposerDraft): boolean =>
  draft.attachments.length === 0 &&
  !draft.audioData &&
  !draft.replyingToMessage &&
  (!draft.stagedQuickMessageMedia || draft.stagedQuickMessageMedia.length === 0);

const enforceCap = (): void => {
  const ids = Object.keys(textDrafts);
  if (ids.length <= MAX_ENTRIES) return;
  ids
    .sort((a, b) => (textDrafts[a]?.updatedAt ?? 0) - (textDrafts[b]?.updatedAt ?? 0))
    .slice(0, ids.length - MAX_ENTRIES)
    .forEach((id) => {
      delete textDrafts[id];
      summaryCache.delete(id);
    });
};

// Cap também no init: localStorage pode já conter >100 entradas válidas (edição
// manual, versão anterior sem cap, import) — prune por idade sozinho não cobre isso.
enforceCap();

export const chatDraftStore = {
  getText(conversationId: string): string {
    return textDrafts[conversationId]?.text ?? '';
  },

  setText(conversationId: string, text: string): void {
    if (text.trim() === '') {
      if (!textDrafts[conversationId]) return;
      delete textDrafts[conversationId];
    } else {
      textDrafts[conversationId] = { text, updatedAt: Date.now() };
      enforceCap();
    }
    persist();
    invalidate(conversationId);
  },

  getComposer(conversationId: string): ComposerDraft | null {
    return composerDrafts.get(conversationId) ?? null;
  },

  setComposer(conversationId: string, draft: ComposerDraft): void {
    if (isComposerEmpty(draft)) {
      if (!composerDrafts.has(conversationId)) return;
      composerDrafts.delete(conversationId);
    } else {
      composerDrafts.set(conversationId, draft);
    }
    invalidate(conversationId);
  },

  /**
   * Limpeza da camada composer no sucesso do envio — o texto é responsabilidade
   * do submit síncrono do ChatInput (limpar texto aqui apagaria o que o usuário
   * digitou DURANTE o envio em voo). Não toca localStorage — camada texto não é
   * responsabilidade deste método.
   */
  clearComposer(conversationId: string): void {
    composerDrafts.delete(conversationId);
    invalidate(conversationId);
  },

  clearEverything(): void {
    textDrafts = {};
    composerDrafts.clear();
    summaryCache.clear();
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // noop — melhor esforço
    }
    emit();
  },

  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /**
   * Resumo estável por referência (cacheado por conversa) — obrigatório para o
   * getSnapshot do useSyncExternalStore não entrar em loop de re-render.
   */
  getDraftSummary(conversationId: string): DraftSummary | null {
    if (summaryCache.has(conversationId)) {
      return summaryCache.get(conversationId) ?? null;
    }
    const text = textDrafts[conversationId]?.text ?? null;
    const composer = composerDrafts.get(conversationId);
    const hasMedia =
      !!composer &&
      (composer.attachments.length > 0 ||
        !!composer.audioData ||
        !!(composer.stagedQuickMessageMedia && composer.stagedQuickMessageMedia.length > 0));
    const summary: DraftSummary | null = text !== null || hasMedia ? { text, hasMedia } : null;
    summaryCache.set(conversationId, summary);
    return summary;
  },
};
